import { DateTime } from "luxon";

// The batch size the weeks are probed in — one sequential round trip per week
// would crawl.
const BATCH_WEEKS = 4;

/**
 * Walk forward week by week from a picked session, collecting the same
 * weekday/time as it goes.
 *
 * The walk covers exactly the `need` weeks that follow the pick and stops
 * there. A week the trainer or court is already taken is a HOLE in that series,
 * not a reason to walk further: the client asked for their slot on those
 * weekdays, so a busy week is theirs to fill — with another time that same day,
 * or by hand at the end — and the walk must never hand them an extra week past
 * the series to make the count up on its own.
 *
 * A failed lookup ends the walk early. A broken request is not evidence the
 * slot is taken, so that week is left neither picked nor flagged.
 *
 * @param {string}   fromDate  ISO date of the last pick; the walk starts a week after it.
 * @param {DateTime} deadline  Last day that may be picked, or null for no window.
 * @param {number}   need      How many more sessions to find.
 * @param {Function} findSlot  async (dateISO) => matching slot or null. May throw.
 * @returns {Promise<{matches: Array<{date: string, slot: object}>, skippedDates: string[]}>}
 *          `skippedDates` holds every week inside the series the slot was not
 *          free — each one a day the client can still rescue by picking another
 *          time.
 */
export async function collectWeeklySlots({ fromDate, deadline, need, findSlot }) {
  const matches = [];
  const skippedDates = [];
  if (need <= 0) return { matches, skippedDates };

  const weeks = [];
  let cursor = DateTime.fromISO(fromDate).plus({ days: 7 });
  for (let i = 0; i < need; i += 1) {
    if (deadline && cursor.startOf("day") > deadline) break;
    weeks.push(cursor.toISODate());
    cursor = cursor.plus({ days: 7 });
  }

  const probe = async (dateISO) => {
    try {
      return { slot: (await findSlot(dateISO)) || null };
    } catch {
      return { failed: true };
    }
  };

  for (let i = 0; i < weeks.length; i += BATCH_WEEKS) {
    const batch = weeks.slice(i, i + BATCH_WEEKS);
    const results = await Promise.all(batch.map(probe));
    for (let j = 0; j < batch.length; j += 1) {
      if (results[j].failed) return { matches, skippedDates };
      if (results[j].slot) matches.push({ date: batch[j], slot: results[j].slot });
      else skippedDates.push(batch[j]);
    }
  }

  return { matches, skippedDates };
}

/**
 * Snackbar line for a finished walk: what was added, which weeks were left open
 * because the slot was taken, and whether the client still has picks to make.
 */
export function weeklyRepeatMessage({ addedN, skipped, remaining, unit = "session" }) {
  const sessions = `${addedN} ${addedN === 1 ? unit : `${unit}s`}`;
  // A busy week outranks the count: it is the one thing on the list the client
  // has to come back to, and saying only how many are left to pick reads as if
  // any free day would do.
  if (skipped > 0) return `Added ${sessions} — ${skipped === 1 ? "1 week is" : `${skipped} weeks are`} unavailable, tap to pick another time`;
  return remaining > 0 ? `Added ${sessions} — ${remaining} still to pick` : `Added ${sessions}`;
}
